'use client';

import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';

import { Trade } from '@/api/copy-trading/types';
import { Side } from '@/api/schema';
import { WithTrendColor } from '@/blocks/components/WithTrendColor';
import { FormatAssetName, FormatPercent } from '@/blocks/formatters';
import typography from '@/styles/typography.module.scss';

import styles from './styles.module.scss';

export function TradeCard({ trade }: { trade: Trade }) {
  const tr = useTranslations();
  return (
    <div className={classNames(styles.tradeCard, typography.bodyS)}>
      <TradeCard.TradeDate timestamp={trade.closeDate} />
      <div className={styles.tradeCardSymbolAndType}>
        <TradeCard.SymbolName symbol={trade.symbol} />
        <TradeCard.TradeType side={trade.side} />
      </div>
      <div className={styles.tradeCardProfitContainer}>
        <span>
          {tr.rich('pages.strategyPage.sections.main.widgets.main.tabs.tradingHistory.tradeCard.profitLabel')}
        </span>
        <WithTrendColor negative={trade.roiOnMargin < 0}>
          <FormatPercent value={trade.roiOnMargin} />
        </WithTrendColor>
      </div>
    </div>
  );
}

TradeCard.SymbolName = function TradeCardSymbol({ symbol }: { symbol: string }) {
  return (
    <span className={classNames(typography.subheadingSM, styles.tradeCardSymbolName)}>
      <FormatAssetName assetName={symbol} />
    </span>
  );
};

TradeCard.TradeType = function TradeCardTradeType({ side }: { side: Side }) {
  const tr = useTranslations();
  return (
    <WithTrendColor
      negative={side === Side.SELL}
      className={classNames(styles.tradeCardTradeType, typography.bodyXS)}
    >
      {tr.rich(
        `pages.strategyPage.sections.main.widgets.main.tabs.tradingHistory.tradeCard.tradeType.${
          side === Side.BUY ? 'long' : 'short'
        }`,
      )}
    </WithTrendColor>
  );
};

TradeCard.TradeDate = function TradeDate({ timestamp }: { timestamp: number }) {
  const locale = useLocale();
  const isUnix = (timestamp: number) => timestamp.toString().length === 10;

  const toJsTimestamp = (timestamp: number) => (isUnix(timestamp) ? timestamp * 1000 : timestamp);

  return (
    <div className={styles.tradeCardDate}>
      {new Date(toJsTimestamp(timestamp)).toLocaleDateString(locale, {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
      })}
    </div>
  );
};
